/**
 * Google Analytics 4 (gtag.js) integrado en la aplicación React.
 *
 * El script ya no vive en `public/index.html`: se carga desde aquí para poder
 * controlar la configuración (page_views manuales del router SPA) y adjuntar a
 * cada evento el contexto del navegador/dispositivo.
 *
 * **La cuenta depende del tenant** (`src/config/analytics.config.ts`): un tenant
 * sin `measurementId` no carga gtag.js ni envía nada.
 *
 * Variables de entorno (todas opcionales):
 *  - REACT_APP_GA_MEASUREMENT_ID  id de medición; manda sobre el del tenant
 *  - REACT_APP_GA_DISABLED=true   desactiva por completo el envío
 *  - REACT_APP_GA_DEBUG=true      fuerza debug_mode (DebugView de GA4)
 */
import { getAnalyticsConfig } from '@/config/analytics.config'

declare global {
  interface Window {
    dataLayer?: any[]
    gtag?: (...args: any[]) => void
  }
}

export type EventParams = Record<string, string | number | boolean | undefined | null>

const { measurementId: MEASUREMENT_ID, gtmId: GTM_ID } = getAnalyticsConfig()
const DISABLED = process.env.REACT_APP_GA_DISABLED === 'true'
const DEBUG_MODE = process.env.REACT_APP_GA_DEBUG === 'true' || process.env.NODE_ENV === 'development'
const TENANT = process.env.REACT_APP_LAYOUT ?? 'hospitales'
// Inyectada por craco.config.js desde package.json.
const APP_VERSION = process.env.REACT_APP_VERSION ?? 'unknown'
const SCRIPT_ID = 'ga4-gtag-js'
const SCRIPT_SRC = 'https://www.googletagmanager.com/gtag/js'
const GTM_SCRIPT_ID = 'gtm-js'
const GTM_SCRIPT_SRC = 'https://www.googletagmanager.com/gtm.js'

let initialized = false

export function isAnalyticsEnabled (): boolean {
  return !DISABLED && MEASUREMENT_ID !== '' && typeof window !== 'undefined'
}

function gtag (...args: any[]): void {
  if (typeof window.gtag !== 'function') {
    ensureGtagStub()
  }
  window.gtag?.(...args)
}

/**
 * Snippet oficial de Google: `gtag` empuja el objeto `arguments` (no un array)
 * al dataLayer. Se respeta tal cual para no depender de detalles internos de la
 * librería, y sirve de cola hasta que el script termina de cargar.
 */
function ensureGtagStub (): void {
  window.dataLayer = window.dataLayer ?? []

  if (typeof window.gtag !== 'function') {
    window.gtag = function gtagStub (): void {
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer?.push(arguments)
    }
  }
}

function injectScript (): void {
  // GTM puede haber cargado ya gtag.js; en ese caso basta con reutilizarlo,
  // meter un segundo <script> duplicaría los hits.
  const alreadyLoaded = document.getElementById(SCRIPT_ID) != null ||
    document.querySelector(`script[src^="${SCRIPT_SRC}"]`) != null

  if (alreadyLoaded) {
    return
  }

  const script = document.createElement('script')
  script.id = SCRIPT_ID
  script.async = true
  script.src = `${SCRIPT_SRC}?id=${MEASUREMENT_ID}`
  document.head.appendChild(script)
}

interface BrowserInfo {
  browser_name: string
  browser_version: string
  os_name: string
  device_type: string
  screen_size: string
  pixel_ratio: number
  touch_support: boolean
  lang: string
  timezone: string
}

let browserInfoCache: BrowserInfo | null = null

function detectBrowser (ua: string): { name: string, version: string } {
  const patterns: Array<[string, RegExp]> = [
    ['Edge', /Edg(?:e|A|iOS)?\/([\d.]+)/],
    ['Opera', /(?:OPR|Opera)\/([\d.]+)/],
    ['Samsung Internet', /SamsungBrowser\/([\d.]+)/],
    ['Firefox', /(?:FxiOS|Firefox)\/([\d.]+)/],
    ['Chrome', /(?:CriOS|Chrome)\/([\d.]+)/],
    ['Safari', /Version\/([\d.]+).*Safari/]
  ]

  for (const [name, pattern] of patterns) {
    const match = ua.match(pattern)
    if (match != null) {
      return { name, version: match[1] }
    }
  }

  return { name: 'Unknown', version: '' }
}

function detectOs (ua: string): string {
  if (ua.includes('Windows NT')) return 'Windows'
  if (ua.includes('Android')) return 'Android'
  if (/iPhone|iPad|iPod/.test(ua)) return 'iOS'
  if (ua.includes('Mac OS X')) return 'macOS'
  if (ua.includes('CrOS')) return 'ChromeOS'
  if (ua.includes('Linux')) return 'Linux'
  return 'Unknown'
}

function detectDeviceType (ua: string): string {
  if (/iPad|Tablet|Android(?!.*Mobi)/i.test(ua)) return 'tablet'
  if (/Mobi|iPhone|iPod|Android/i.test(ua)) return 'mobile'
  return 'desktop'
}

function getBrowserInfo (): BrowserInfo {
  if (browserInfoCache != null) {
    return browserInfoCache
  }

  const ua = window.navigator.userAgent
  const browser = detectBrowser(ua)

  browserInfoCache = {
    browser_name: browser.name,
    browser_version: browser.version,
    os_name: detectOs(ua),
    device_type: detectDeviceType(ua),
    screen_size: `${window.screen.width}x${window.screen.height}`,
    pixel_ratio: window.devicePixelRatio,
    touch_support: window.navigator.maxTouchPoints > 0,
    lang: window.navigator.language,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
  }

  return browserInfoCache
}

function getConnectionType (): string {
  const connection = (window.navigator as any).connection
  return connection?.effectiveType ?? 'unknown'
}

/**
 * Contexto que viaja en TODOS los eventos: navegador, dispositivo, pantalla,
 * tenant white-label y estado de la ventana en el momento del evento.
 */
function getEventContext (): EventParams {
  return {
    ...getBrowserInfo(),
    tenant: TENANT,
    app_version: APP_VERSION,
    viewport_size: `${window.innerWidth}x${window.innerHeight}`,
    connection_type: getConnectionType()
  }
}

function cleanParams (params: EventParams): EventParams {
  const cleaned: EventParams = {}

  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '') {
      cleaned[key] = value
    }
  }

  return cleaned
}

/**
 * Snippet oficial de Google Tag Manager, movido aquí desde index.html para que
 * el contenedor dependa del tenant: cada cliente tiene el suyo, y el de uno no
 * debe cargarse en la web de otro.
 */
function initTagManager (): void {
  if (DISABLED || GTM_ID === '') {
    return
  }

  if (document.getElementById(GTM_SCRIPT_ID) != null) {
    return
  }

  window.dataLayer = window.dataLayer ?? []
  window.dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' })

  const script = document.createElement('script')
  script.id = GTM_SCRIPT_ID
  script.async = true
  script.src = `${GTM_SCRIPT_SRC}?id=${GTM_ID}`
  document.head.appendChild(script)
}

export function initAnalytics (): void {
  if (initialized || typeof window === 'undefined') {
    return
  }

  initialized = true

  initTagManager()
  initGa4()
}

function initGa4 (): void {
  if (!isAnalyticsEnabled()) {
    return
  }

  ensureGtagStub()
  injectScript()

  const context = getEventContext()

  gtag('js', new Date())
  // Parámetros globales: se adjuntan a cualquier evento posterior.
  gtag('set', context)
  gtag('set', 'user_properties', {
    tenant: TENANT,
    browser_name: context.browser_name,
    os_name: context.os_name,
    device_type: context.device_type
  })
  gtag('config', MEASUREMENT_ID, {
    // Las page_view las dispara el router (SPA): ver AnalyticsTracker.
    send_page_view: false,
    debug_mode: DEBUG_MODE,
    ...context
  })
}

export function trackEvent (name: string, params: EventParams = {}): void {
  if (!isAnalyticsEnabled()) {
    return
  }

  if (!initialized) {
    initAnalytics()
  }

  gtag('event', name, cleanParams({ ...getEventContext(), ...params }))
}

export interface PharmacyCounts {
  total: number
  internal: number
  external: number
}

/**
 * Señales de disponibilidad del flujo principal: sin farmacias no hay reserva
 * posible, y si solo hay externas el usuario tampoco puede encargar (no hay
 * stock en tiempo real). Se emite además del evento principal de cada camino.
 */
export function trackPharmacyAvailability (counts: PharmacyCounts, source: string, params: EventParams = {}): void {
  const common = {
    source,
    pharmacies_count: counts.total,
    internal_pharmacies: counts.internal,
    external_pharmacies: counts.external,
    ...params
  }

  if (counts.total === 0) {
    trackEvent('pharmacies_not_found', common)
    return
  }

  if (counts.internal === 0) {
    trackEvent('pharmacies_only_external', common)
  }
}

/**
 * Plantilla de la ruta. En GA4 el informe de paginas agrupa por `page_location`,
 * asi que mandar la URL literal convierte cada receta en una fila distinta
 * (`/uRcrJI2G1PwB`, `/VsaSTNRhOdre`...). Se manda la plantilla y el id real
 * viaja aparte, en `prescription_id`.
 */
export function getRoute (pathname: string): string {
  const segments = pathname.split('/').filter((segment) => segment !== '')

  if (segments.length === 1) {
    const [first] = segments
    return first === 'success' || first === 'error' ? `/${first}` : '/:prescriptionId'
  }

  if (segments.length === 2) {
    const [, second] = segments

    if (second === 'success' || second === 'error') {
      return `/:prescriptionId/${second}`
    }
  }

  return pathname
}

const RUTAS_CON_RECETA = ['/:prescriptionId', '/:prescriptionId/success', '/:prescriptionId/error']

function getPrescriptionId (pathname: string): string | undefined {
  if (!RUTAS_CON_RECETA.includes(getRoute(pathname))) {
    return undefined
  }

  const [first] = pathname.split('/').filter((segment) => segment !== '')

  return first
}

export function trackPageView (pathname: string, params: EventParams = {}): void {
  // PUBLIC_URL vale '/' en los tenants servidos en la raíz del dominio; el
  // router entrega pathname sin el basename, así que se vuelve a componer.
  const basePath = (process.env.PUBLIC_URL ?? '').replace(/\/$/, '')
  const route = getRoute(pathname)

  trackEvent('page_view', {
    // Sin query: el de /success lleva la dirección del paciente y los nombres
    // de los medicamentos.
    page_path: `${basePath}${route}`,
    page_location: `${window.location.origin}${basePath}${route}`,
    page_title: document.title,
    page_referrer: document.referrer,
    route,
    prescription_id: getPrescriptionId(pathname),
    ...params
  })
}
